/**
 * La card de KPI del BO. Auditoría 18/09/2026: había 12 copias (FinOps,
 * Observability, Internal, Subscriptions ×4, Skills, D-Talk, Learning, TA ×2)
 * con la misma silueta y cinco paletas de tonos distintas. Esta es la única;
 * `audit-reuse` (regla `statcard-local`) frena la número 13.
 *
 *  - `tone`: caja del ícono (paleta común). `iconClassName` para un color
 *    puntual (`text-violet-500`), `iconBox={false}` para el ícono suelto.
 *  - `fill`: pinta la card entera (Learning: verde/naranja/rojo del doc).
 *  - `trend` + `trendLabel(v)`: pie con flecha y el texto traducido del módulo.
 *  - `layout="left"`: ícono a la izquierda (Skills, TA).
 */
import type { ElementType, ReactNode } from 'react';
import { isValidElement } from 'react';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export type StatTone = 'blue' | 'purple' | 'green' | 'amber' | 'rose' | 'default' | 'good' | 'warn' | 'bad' | 'primary';
export type StatFill = 'neutral' | 'green' | 'amber' | 'orange' | 'red';

const TONE: Record<StatTone, string> = {
  blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30',
  purple: 'bg-purple-50 text-purple-600 dark:bg-purple-900/30',
  green: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
  amber: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
  rose: 'bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400',
  default: 'bg-muted text-muted-foreground',
  good: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
  warn: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
  bad: 'bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400',
  primary: 'bg-primary/10 text-primary',
};

const FILL: Record<StatFill, string> = {
  neutral: '',
  green: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200',
  amber: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
  orange: 'border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-800 dark:bg-orange-900/30 dark:text-orange-200',
  red: 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200',
};

export interface StatCardProps {
  label: string;
  value: string | number;
  /** Línea chica debajo del valor (antes `sub` o `hint`). */
  sub?: string;
  /** Componente de lucide (`icon={Users}`) o un nodo ya renderizado (`icon={<Users className="h-4 w-4" />}`). */
  icon?: ElementType | ReactNode;
  tone?: StatTone;
  /** Clases extra para el ícono (color puntual). */
  iconClassName?: string;
  /** false: el ícono va suelto, sin la cajita. */
  iconBox?: boolean;
  fill?: StatFill;
  /** Variación (%): flecha + color; el texto lo da `trendLabel` (traducido por el módulo). */
  trend?: number | null;
  trendLabel?: (trend: number) => string;
  layout?: 'right' | 'left';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

function renderIcon(icon: StatCardProps['icon'], className: string): ReactNode {
  if (!icon) return null;
  if (isValidElement(icon)) return icon;
  const Icon = icon as ElementType;
  return <Icon className={className} />;
}

const VALUE_SIZE = { sm: 'text-xl', md: 'text-2xl', lg: 'text-3xl' } as const;

export function StatCard({
  label, value, sub, icon, tone = 'blue', iconClassName, iconBox = true, fill = 'neutral',
  trend, trendLabel, layout = 'right', size = 'md', className,
}: StatCardProps) {
  const filled = fill !== 'neutral';
  const muted = filled ? 'opacity-80' : 'text-muted-foreground';
  const iconNode = icon
    ? iconBox
      ? (
        <div className={cn('flex shrink-0 items-center justify-center', layout === 'left' ? 'h-10 w-10 rounded-xl' : 'h-9 w-9 rounded-lg', TONE[tone], iconClassName)}>
          {renderIcon(icon, 'h-4 w-4')}
        </div>
      )
      : renderIcon(icon, cn('mt-0.5 h-6 w-6 shrink-0', iconClassName))
    : null;
  const text = (
    <div className="min-w-0 flex-1">
      <div className={cn('text-xs font-medium uppercase tracking-wide', muted)}>{label}</div>
      <div className={cn('mt-1 truncate font-bold tabular-nums', VALUE_SIZE[size])}>{value}</div>
      {sub && <div className={cn('mt-1 truncate text-xs', muted)}>{sub}</div>}
    </div>
  );
  return (
    <Card className={cn('p-4', FILL[fill], className)}>
      {layout === 'left' ? (
        <div className="flex items-start gap-3">{iconNode}{text}</div>
      ) : (
        <div className="flex items-start justify-between gap-2">{text}{iconNode}</div>
      )}
      {trend != null && (
        <div className={cn('mt-2 flex items-center gap-1 text-xs font-medium', trend >= 0 ? 'text-green-600' : 'text-red-500')}>
          {trend >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          {trendLabel ? trendLabel(trend) : `${trend >= 0 ? '+' : ''}${trend.toFixed(1)}%`}
        </div>
      )}
    </Card>
  );
}
